// The organisation plane (ADR-0021).
//
// An organisation is an identity and a staff roster; a workspace is a billing,
// content and security boundary. They have different lifetimes, which is why
// ADR-0021 split them: staff outlive workspaces, identity spans them, and an
// agency wanting two of its own workspaces should not need two rosters and two
// handles to get them.
//
// THE RULE EVERY CALL HERE OBEYS: an organisation role grants no permission
// inside any workspace, including the organisation's own. Being an agency admin
// lets you assign people; it does not let you publish. Reaching a workspace is
// still an explicit role assignment for a named person, exactly as ADR-0011 has
// it, and nothing in this file changes that.

import { supabase } from "./supabase";
import { asAppError } from "./entitlements";

/** Ordered, unlike workspace roles: owner ⊃ admin ⊃ manager ⊃ member. Four
 *  levels and no custom roles, so a lattice would be ceremony. */
export type OrgRole = "owner" | "admin" | "manager" | "member";

/** `personal` is an origin and a default, never an authority. It marks the
 *  organisation signup created — the user's home, where anything without a
 *  stated owner lands — and is read only by the transfer and delete guards. */
export type OrgKind = "personal" | "agency" | "brand" | "network";

export const ORG_ROLE_LABEL: Record<OrgRole, string> = {
  owner: "Owner",
  admin: "Admin",
  manager: "Manager",
  member: "Member",
};

export const ORG_ROLE_BLURB: Record<OrgRole, string> = {
  owner: "Everything, including transferring the organisation.",
  admin: "The roster, the teams, and staffing every client.",
  manager: "Staffing only the clients they are assigned to.",
  member: "Belongs to the organisation and can be staffed. Administers nothing.",
};

export interface Organisation {
  organisation_id: string;
  name: string;
  handle: string | null;
  kind: OrgKind;
  org_role: OrgRole;
  is_owner: boolean;
  workspaces: number;
  clients: number;
}

export interface OrganisationInvitation {
  id: string;
  organisation_id: string;
  organisation_name: string;
  org_role: OrgRole;
  invited_by_email: string | null;
  created_at: string;
  expires_at: string;
}

/**
 * The seat sum, itemised.
 *
 * Itemised deliberately: "you have 7 seats" invites an argument, and "1
 * included, 4 earned by 43 connections, 2 bought" answers it before it starts.
 */
export interface Seats {
  base: number;
  connections: number;
  ratio: number;
  from_connections: number;
  purchased: number;
  allowed: number;
  used: number;
}

export async function myOrganisations(): Promise<Organisation[]> {
  const { data, error } = await supabase.rpc("my_organisations");
  if (error) throw asAppError(error);
  return (data ?? []) as Organisation[];
}

export async function createOrganisation(args: {
  name: string;
  handle?: string | null;
  kind?: Exclude<OrgKind, "personal">;
}): Promise<{ id: string; name: string; handle: string | null }> {
  const { data, error } = await supabase.rpc("create_organisation", {
    p_name: args.name,
    p_handle: args.handle ?? null,
    p_kind: args.kind ?? "agency",
  });
  if (error) throw asAppError(error);
  return data as { id: string; name: string; handle: string | null };
}

/**
 * Claim the handle, once.
 *
 * It stops being changeable the moment a client authorises on the strength of
 * it: the identity somebody agreed to must be the identity that persists, and a
 * freed handle goes to whoever claims it next (ADR-0021 L-D).
 */
export async function setOrganisationHandle(
  organisationId: string, handle: string,
): Promise<string | null> {
  const { data, error } = await supabase.rpc("set_organisation_handle", {
    p_organisation_id: organisationId, p_handle: handle,
  });
  if (error) throw asAppError(error);
  return (data ?? null) as string | null;
}

export async function inviteToOrganisation(args: {
  organisationId: string;
  email: string;
  orgRole: Exclude<OrgRole, "owner">;
}): Promise<OrganisationInvitation> {
  const { data, error } = await supabase.rpc("invite_to_organisation", {
    p_organisation_id: args.organisationId,
    p_email: args.email,
    p_org_role: args.orgRole,
  });
  if (error) throw asAppError(error);
  return data as OrganisationInvitation;
}

/** Findable after you sign in, not only in an email that may never arrive —
 *  matched against your own confirmed address, never against a claim. */
export async function myOrganisationInvitations(): Promise<OrganisationInvitation[]> {
  const { data, error } = await supabase.rpc("my_organisation_invitations");
  if (error) throw asAppError(error);
  return (data ?? []) as OrganisationInvitation[];
}

export async function acceptOrganisationInvitation(invitationId: string): Promise<{
  organisation_id: string; organisation_name: string; org_role: OrgRole;
}> {
  const { data, error } = await supabase.rpc("accept_organisation_invitation", {
    p_invitation_id: invitationId,
  });
  if (error) throw asAppError(error);
  return data as { organisation_id: string; organisation_name: string; org_role: OrgRole };
}

export async function setOrganisationMemberRole(args: {
  organisationId: string; userId: string; orgRole: Exclude<OrgRole, "owner">;
}): Promise<void> {
  const { error } = await supabase.rpc("set_organisation_member_role", {
    p_organisation_id: args.organisationId,
    p_user_id: args.userId,
    p_org_role: args.orgRole,
  });
  if (error) throw asAppError(error);
}

export async function transferOrganisationOwnership(
  organisationId: string, newOwnerId: string,
): Promise<void> {
  const { error } = await supabase.rpc("transfer_organisation_ownership", {
    p_organisation_id: organisationId, p_new_owner_id: newOwnerId,
  });
  if (error) throw asAppError(error);
}

export async function deleteOrganisation(organisationId: string): Promise<void> {
  const { error } = await supabase.rpc("delete_organisation", {
    p_organisation_id: organisationId,
  });
  if (error) throw asAppError(error);
}

export async function organisationSeats(organisationId: string): Promise<Seats> {
  const { data, error } = await supabase.rpc("organisation_seats", {
    p_organisation_id: organisationId,
  });
  if (error) throw asAppError(error);
  return data as Seats;
}

// ------------------------------------------------------------------ teams

export interface Team {
  id: string;
  organisation_id: string;
  name: string;
}

export async function createTeam(organisationId: string, name: string): Promise<Team> {
  const { data, error } = await supabase.rpc("create_team", {
    p_organisation_id: organisationId, p_name: name,
  });
  if (error) throw asAppError(error);
  return data as Team;
}

/**
 * Put somebody in a pod, or take them out.
 *
 * Returns how many client workspaces it touched, because that is the honest
 * size of the action: one membership change is an access change at every client
 * that pod serves. Each of those clients gets an audit row in their own trail
 * for the same reason — an agency quietly adding a seventh person to somebody's
 * Facebook Page is precisely what the engagement model exists to prevent.
 */
export async function setTeamMembership(args: {
  teamId: string; userId: string; member: boolean;
}): Promise<{ workspaces_affected: number; workspace_ids: string[] }> {
  const { data, error } = await supabase.rpc("set_team_membership", {
    p_team_id: args.teamId, p_user_id: args.userId, p_member: args.member,
  });
  if (error) throw asAppError(error);
  return data as { workspaces_affected: number; workspace_ids: string[] };
}

// ------------------------------------------------------------- workspaces

export async function createWorkspace(args: {
  name: string;
  organisationId?: string | null;
  kind?: "influencer" | "business" | "agency";
}): Promise<{ id: string; name: string }> {
  const { data, error } = await supabase.rpc("create_workspace", {
    p_name: args.name,
    p_organisation_id: args.organisationId ?? null,
    p_kind: args.kind ?? "business",
  });
  if (error) throw asAppError(error);
  return data as { id: string; name: string };
}

export async function transferWorkspaceOwnership(
  workspaceId: string, newOwnerId: string,
): Promise<void> {
  const { error } = await supabase.rpc("transfer_workspace_ownership", {
    p_workspace_id: workspaceId, p_new_owner_id: newOwnerId,
  });
  if (error) throw asAppError(error);
}

/**
 * Move a workspace between organisations.
 *
 * Allowed, because the alternative — export the content and rebuild — is worse
 * in every way. Fenced, because it crosses a security boundary: it needs
 * `workspace.transfer` on the workspace AND admin in the destination, and it
 * **ends every engagement on that workspace**. An agency was authorised by this
 * organisation's client; carrying that authorisation into a new parent would
 * silently re-point it at people the client never agreed to.
 */
export async function moveWorkspaceToOrganisation(
  workspaceId: string, organisationId: string | null,
): Promise<{ moved: boolean; engagements_ended: number }> {
  const { data, error } = await supabase.rpc("move_workspace_to_organisation", {
    p_workspace_id: workspaceId, p_organisation_id: organisationId,
  });
  if (error) throw asAppError(error);
  return data as { moved: boolean; engagements_ended: number };
}

// --------------------------------------------------------- connection shares

/**
 * One account, lent to one organisation (ADR-0022 §3).
 *
 * Addressed to an organisation rather than a person, and that is a real trade
 * made deliberately. Lending to a named human means you choose the individual
 * and nothing changes without you — and when they leave that agency, nothing
 * tells you. An organisation has an offboarding story: removing somebody from
 * it ends every borrowed access in one transaction. The price is that *they*
 * choose who, which is why `connection_access` always names the humans.
 */
export interface ConnectionShare {
  share_id: string;
  organisation_id: string;
  organisation: string;
  role: string;
  expires_at: string | null;
  people: { user_id: string; email: string | null; org_role: OrgRole }[];
}

/** Everyone who can reach one account, split by *why* — because "why do they
 *  have this" is the second question and a merged list cannot answer it. */
export interface ConnectionAccess {
  connection_id: string;
  display_name: string;
  workspace_id: string;
  in_workspace: { user_id: string; email: string | null; role: string; scope: string }[];
  via_engagement: { user_id: string; email: string | null; role: string; organisation: string }[];
  shared_with: ConnectionShare[];
}

export async function connectionAccess(connectionId: string): Promise<ConnectionAccess | null> {
  const { data, error } = await supabase.rpc("connection_access", {
    p_connection_id: connectionId,
  });
  if (error) throw asAppError(error);
  return (data ?? null) as ConnectionAccess | null;
}

/**
 * Lend it. The bar is `connection.disconnect`, not `connection.view`: lending an
 * account is an act of the same weight as ending it, and somebody who may merely
 * look at a Page has no business handing it to a third party.
 */
export async function shareConnection(args: {
  connectionId: string;
  toOrganisationId: string;
  role?: string;
  expiresAt?: string | null;
  note?: string | null;
}): Promise<ConnectionShare> {
  const { data, error } = await supabase.rpc("share_connection", {
    p_connection_id: args.connectionId,
    p_to_organisation_id: args.toOrganisationId,
    p_role_code: args.role ?? "publisher",
    p_expires_at: args.expiresAt ?? null,
    p_note: args.note ?? null,
  });
  if (error) throw asAppError(error);
  return data as ConnectionShare;
}

/** Either side may end it: the owner can always take it back, and the borrower
 *  can always hand it back without having to ask. */
export async function revokeConnectionShare(shareId: string): Promise<void> {
  const { error } = await supabase.rpc("revoke_connection_share", { p_share_id: shareId });
  if (error) throw asAppError(error);
}
